// Detect doc-viewer-eligible paths inside arbitrary text (D-012,
// D-019). Widened from `.md`-only to the full allowlist defined in
// `backend/src/docs/path-safety.ts`: `.md`, `.txt`, `.json`, `.png`,
// `.jpg`, `.jpeg`, `.webp`, `.gif`, `.pdf`. The function name is kept
// for backwards compatibility with call sites (UserMessage,
// MarkdownRenderer) — the detector now matches every extension the
// viewer overlay can render.
//
// Conservative by design: false negatives are acceptable (Santi can ask
// the agent to repeat the path), false positives are not (a phantom chip
// in the chat is noise). The function returns plain ranges; rendering
// stays in the UI layer.
//
// The regex matches a permissive candidate; post-filters reject:
//   - bare filenames without a `/` (a path names a location, not just a name)
//   - `..` segments (traversal — defense-in-depth; the backend rejects too)
//   - candidates whose surrounding chars would make them substrings of a
//     larger token (`version.md5`, `file.md.bak`, `dir.md/sub`)

export interface DetectedPath {
  start: number;
  end: number;
  path: string;
}

// Allowed extensions — must stay in lockstep with
// `backend/src/docs/path-safety.ts` ALLOWED_DOC_EXTENSIONS. The frontend
// has no compile-time link to the backend module (no shared package);
// the path-safety test on the backend side enforces parity in CI.
const ALLOWED_EXTENSIONS = [
  'md',
  'txt',
  'json',
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'pdf',
] as const;

// Build the regex once at module load. Case-insensitive match against
// any allowed extension; the surrounding boundary checks below still
// run per-candidate.
const PATH_CANDIDATE = new RegExp(
  `[./\\w\\-]+\\.(?:${ALLOWED_EXTENSIONS.join('|')})\\b`,
  'gi',
);
const PATH_BOUNDARY_CHAR = /[./\w\-]/;

const MAX_TEXT_LENGTH = 64 * 1024;

export function detectMdPaths(text: string): DetectedPath[] {
  if (typeof text !== 'string' || text.length === 0) return [];
  // Cap to keep the regex bounded in practice. A single message larger
  // than this is already abnormal; we degrade to "no chips" rather than
  // letting the matcher run unbounded.
  if (text.length > MAX_TEXT_LENGTH) return [];

  const out: DetectedPath[] = [];
  for (const match of text.matchAll(PATH_CANDIDATE)) {
    const value = match[0];
    const start = match.index ?? 0;
    const end = start + value.length;

    // Must name a directory + file, not just a bare filename.
    if (!value.includes('/')) continue;
    // Reject traversal segments.
    if (value.includes('..')) continue;

    // Left boundary: the char immediately before must not be another path
    // character (otherwise the regex matched mid-word).
    const prev = start > 0 ? text[start - 1] : undefined;
    if (prev !== undefined && PATH_BOUNDARY_CHAR.test(prev)) continue;

    // Right boundary:
    //   - a word char (e.g. `5` after `.md` → `version.md5`) → reject
    //   - a slash (e.g. `/sub` after `.md` → `dir.md/sub`) → reject
    //   - a `.<word>` chain (e.g. `.bak` after `.md` → `file.md.bak`) → reject
    //   - sentence-terminating `.` followed by whitespace / EOS → accept
    const next = end < text.length ? text[end] : undefined;
    if (next !== undefined) {
      if (/\w/.test(next)) continue;
      if (next === '/') continue;
      if (next === '.') {
        const nextNext = end + 1 < text.length ? text[end + 1] : undefined;
        if (nextNext !== undefined && /\w/.test(nextNext)) continue;
      }
    }

    out.push({ start, end, path: value });
  }
  return out;
}

// Convenience alias — the function above used to detect `.md` paths
// only; it now detects every allowed doc-viewer extension. The `Md`
// name is kept for backwards compat with call sites that import it;
// new code should prefer `detectDocPaths` for clarity.
export const detectDocPaths = detectMdPaths;
